import React, { useEffect, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import {
    useChecklistContext,
    useChecklistItemDragging,
    useChecklistItemErrorHandle,
    useChecklistItemHandlers,
    useChecklistItemInput,
} from '@features/Checklist/model/hooks';
import { IChecklistTask } from '@features/Checklist/model/types';
import { Checkbox, DraggableEl, Flex, Icon, ModalTitle, UiButton } from '@shared/ui';

import cls from './ChecklistItem.module.css';

interface IChecklistItemProps {
    canEdit: boolean;
    itemData: IChecklistTask;
}
export const ChecklistItem = ({ canEdit, itemData }: IChecklistItemProps) => {
    const { t } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);
    const { isDraggingChecklist, lastAddedChecklistItemClientId } = useChecklistContext();
    const { ref, blurModalTitle } = useChecklistItemInput();
    const {
        errorValidation,
        clearError,
        triggerAnimationError,
        addError,
        setTriggerAnimationError,
    } = useChecklistItemErrorHandle();
    const {
        onAddCheckListItemHandler,
        onCheckedItem,
        onDeleteCheckListItemHandler,
        onChangeChecklistItemHandler,
        onModalTitleKeyDown,
    } = useChecklistItemHandlers({
        itemData,
        modalTitleKeydownCb: blurModalTitle,
        isError: errorValidation.isError,
        addErrorCb: addError,
        clearErrorCb: clearError,
    });
    const { setNodeRef, setActivatorNodeRef, style, listeners, attributes, isDragging } =
        useChecklistItemDragging({ itemData, canEdit });

    const canShowActions = canEdit && !isDraggingChecklist;
    const shouldAutoFocusNewItem =
        canEdit &&
        !itemData.isChecked &&
        itemData.text.length === 0 &&
        lastAddedChecklistItemClientId === itemData.clientId;

    useEffect(() => {
        if (isDraggingChecklist) {
            setIsHovered(false);
        }
    }, [isDraggingChecklist]);

    return (
        <Flex
            as="li"
            justify="between"
            align="start"
            gap={6}
            key={itemData.clientId}
            className={cn(
                cls.ChecklistItem,
                itemData.isChecked && cls.isChecked,
                isDragging && cls.isDragging
            )}
            ref={setNodeRef}
            style={style}
            onClick={onCheckedItem}
            onMouseEnter={() => {
                if (canShowActions) {
                    setIsHovered(true);
                }
            }}
            onMouseLeave={() => {
                if (canShowActions) {
                    setIsHovered(false);
                }
            }}
        >
            <Flex fullWidth align="start" gap={6}>
                {canEdit && (
                    <DraggableEl
                        ref={setActivatorNodeRef}
                        {...attributes}
                        {...listeners}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Dragging checklist item"
                    />
                )}
                <Checkbox isActive={itemData.isChecked} />
                <ModalTitle
                    ref={ref}
                    data-checklist-modal-title="true"
                    data-checklist-item-client-id={itemData.clientId}
                    autoFocus={shouldAutoFocusNewItem}
                    isError={errorValidation.isError}
                    errorClassName={cls.ChecklistItemError}
                    onChangeHandler={(e) => onChangeChecklistItemHandler(e.target.value)}
                    placeholder={t('Checklist.Item.InputPlaceholder')}
                    classes={cn(cls.ChecklistItemText, itemData.isChecked && cls.isChecked)}
                    value={itemData.text}
                    triggerAnimationError={triggerAnimationError}
                    setTriggerAnimationError={setTriggerAnimationError}
                    readOnly={!canEdit || itemData.isChecked}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (canEdit) {
                            return;
                        }
                        onCheckedItem();
                    }}
                    onKeyDown={onModalTitleKeyDown}
                />
                {canShowActions && isHovered && (
                    <UiButton
                        className={cls.AddCheckListBtn}
                        startIcon={<Icon iconName="plus" width={10} height={10} fill="white" />}
                        btnText={t('Checklist.AddItem.span.text')}
                        onClick={onAddCheckListItemHandler}
                    />
                )}
            </Flex>
            <UiButton
                className={cn(
                    cls.DeleteChecklistItemBtn,
                    canShowActions && isHovered && cls.revealBtn
                )}
                startIcon={<Icon iconName="taskDelete" fill="#818998" width={14} height={14} />}
                onClick={onDeleteCheckListItemHandler}
            />
        </Flex>
    );
};
